import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useAdvancedMarkerRef,
  useMapsLibrary,
  useMap,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { connect } from "react-redux";
import { selectLeadgen } from "../../selectors";
import { Typography, Box, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { selectLeads } from "../../../leads/selectors";
import styles from "../../Leadgen.styles";
import type { RootState } from '../../../../reducers';
import { MapPin } from './MapPin';

const { REACT_APP_API_KEY } = process.env;

interface MapsProps {
  mapStyles: object;
  currentObject?: object;
  entityObjectsList?: any;
  addressForMapDisplaying?: string;
  mapRequestId?: number;
  currentLeadId?: any;
  tab?: string;
}

const defaultCenterAmsterdam = {
  lat: 52.3679843,
  lng: 4.903561399999944,
};

const normalizeAddress = (value: unknown) => {
  return String(value ?? "").trim();
};

const isNotValidAddress = (value: string) => {
  const str = value.trim().toLowerCase();
  if (!str) return true;
  return (
      str === "na" ||
      str === "n/a" ||
      str === "null" ||
      str === "undefined" ||
      str.includes("undefined") ||
      str.includes("null") ||
      str.includes("n/a")
  );
};

const FALLBACK_DELAY_MS = 300;
const OVERLAY_SHOW_DELAY_MS = 250;
const OVERLAY_MAX_MS = 3000;

const getObjectAddress = (currentObject: any) => {
  const city: string = (currentObject?.city ?? "").toString().trim();
  const objectAddress: string = (currentObject?.objectAddress ?? "").toString().trim();
  const combinedAddress = `${city} ${objectAddress}`;
  return combinedAddress;
};

function Geocoding({
    address,
    mapStyles,
    mapRequestId,
    shouldFallbackToDefault
  }:{
    address: any;
    mapStyles?: any;
    mapRequestId?: number;
    shouldFallbackToDefault: boolean;
  }) {
  const map = useMap();
  const geocodingApiLoaded = useMapsLibrary("geocoding");
  const [geocodingService, setGeocodingService] =
    useState<google.maps.Geocoder | null>(null);
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [resolvedPos, setResolvedPos] = useState<{ lat: number; lng: number }>(defaultCenterAmsterdam);
  const [showInitialOverlay, setShowInitialOverlay] = useState<boolean>(false);
  const [infoOwnerRequestId, setInfoOwnerRequestId] = useState<number | null>(null);

  const handleMarkerClick = useCallback(() => {
    setInfoOwnerRequestId(mapRequestId ?? null);
    setOpenInfo(true);
  }, [mapRequestId]);

  useLayoutEffect(() => {
    setOpenInfo(false);
    setInfoOwnerRequestId(null);
  }, [mapRequestId]);

  const isInfoOpen = openInfo && infoOwnerRequestId === (mapRequestId ?? null);

  useEffect(() => {
    if (!geocodingApiLoaded) return;
    if (!map) return;
    setGeocodingService(new window.google.maps.Geocoder());
  }, [geocodingApiLoaded, map]);

  useEffect(() => {
    if (!map) return;
    map.panTo(resolvedPos);
  }, [map, resolvedPos, mapRequestId]);

  useEffect(() => {
    const showT = window.setTimeout(() => {
      setShowInitialOverlay(true);
    }, OVERLAY_SHOW_DELAY_MS);
    const hideT = window.setTimeout(() => {
      setShowInitialOverlay(false);
    }, OVERLAY_MAX_MS);
    return () => {
      window.clearTimeout(showT);
      window.clearTimeout(hideT);
    }
  }, [mapRequestId]);

  useEffect(() => {
    if (!shouldFallbackToDefault) return;
    const t = window.setTimeout(() => {
      setResolvedPos(defaultCenterAmsterdam);
      setOpenInfo(false);
    }, FALLBACK_DELAY_MS);

    return () => window.clearTimeout(t);
  }, [shouldFallbackToDefault, mapRequestId]);

  useEffect(() => {
    if (shouldFallbackToDefault) return;
    if (!geocodingService) return;
    if (!address) return;

    let cancelled = false;

    (async () => {

      try {
        const response = await geocodingService.geocode({
          address: address,
        });
        if (cancelled) return;
        const result = response.results?.[0] ?? null;
        const loc = result?.geometry?.location;
        if (!loc) {
          setOpenInfo(false);
          setResolvedPos(defaultCenterAmsterdam);
          return;
        } else {
          setResolvedPos({lat: loc.lat(), lng: loc.lng()});
        }
        setOpenInfo(false);
        setShowInitialOverlay(false);
      } catch {
        setOpenInfo(false);
        setResolvedPos(defaultCenterAmsterdam);
        setShowInitialOverlay(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [geocodingService, address, mapRequestId, shouldFallbackToDefault]);

  return (
    <Box sx={{ position: 'relative', ...mapStyles }}>
        <Map
          style={styles.stackHolderContainer}
          defaultCenter={defaultCenterAmsterdam}
          defaultZoom={20}
          gestureHandling={"greedy"}
          disableDefaultUI={false}
          mapId={"LEADGEN_MAP"}
        >

          <AdvancedMarker
            ref={markerRef}
            position={resolvedPos}
            onClick={handleMarkerClick}
          >
            <MapPin />
          </AdvancedMarker>

          {isInfoOpen &&  (
            <InfoWindow
              anchor={marker}
              position={resolvedPos}
              headerDisabled
            >
              <Box sx={styles.boxInfoWindow}>
                <Typography sx={styles.textInfoWindow}>
                  {address
                    ? address
                    : "Center Amsterdam"}
                </Typography>
                <IconButton sx={{ padding: 0 }} onClick={() => setOpenInfo(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </InfoWindow>
          )}
        </Map>
        {showInitialOverlay && (
          <Box sx={styles.boxCircularProgress}>
            <CircularProgress color="primary" size={40} />
          </Box>
        )}
    </Box>
  );
}

const Maps = (props: MapsProps) => {
  const {
    mapStyles,
    currentObject,
    addressForMapDisplaying,
    mapRequestId,
  } = props;
  const addressFromCurrentObject = currentObject ? getObjectAddress(currentObject) : null;
  const normAddressFromCurrentObject = normalizeAddress(addressFromCurrentObject);
  const normAddressForMapDisplaying = normalizeAddress(addressForMapDisplaying);
  const normAddress = normAddressFromCurrentObject || normAddressForMapDisplaying;
  const hasValidAddress = normAddress && !isNotValidAddress(normAddress);
  const shouldFallbackToDefault = !hasValidAddress;
  if (!REACT_APP_API_KEY) {
    throw new Error("Google Maps API key is missing");
  }
  return (
    <APIProvider
      apiKey={REACT_APP_API_KEY}
    >
      <>
          <Geocoding
            address={hasValidAddress ? normAddress : null}
            shouldFallbackToDefault={shouldFallbackToDefault}
            mapRequestId={mapRequestId}
            mapStyles={mapStyles}
          />
      </>
    </APIProvider>
  );
};

function mapStateToProps(state: RootState) {
  const { entityObjectsList, currentObject } = selectLeadgen(state);
  const { currentLeadId } = selectLeads(state);
  return { entityObjectsList, currentObject, currentLeadId };
}

export default connect(mapStateToProps)(Maps);
